import type { ZodMiniType } from "zod/mini";
import type { FormFieldConfig } from "../../../mixitone/src/components/Forms/FormFieldsDefinition";

/**
 * Supported regions for payment methods
 */
export type PaymentRegion = "global" | "nz" | "au" | "us" | "uk" | "eu";

/**
 * Abstract class for payment methods
 *
 * Each payment method should extend this class to provide:
 * - Configuration form fields for admin setup
 * - Description and instructions for customer display
 */
export abstract class PaymentMethod<TConfig = unknown> {
  /**
   * Unique identifier for the payment method
   */
  abstract id: string;

  /**
   * Display name for the payment method
   */
  abstract name: string;

  /**
   * Regions where this payment method is available
   */
  abstract supportedRegions: PaymentRegion[];

  /**
   * Get the form field configurations for this payment method
   * This defines what configuration is collected in the UI
   */
  abstract getConfigFields(): FormFieldConfig[];

  /**
   * Get the Zod schema for this payment method's configuration
   * This defines what configuration is stored in the database
   */
  abstract getConfigSchema(): ZodMiniType<TConfig>;

  /**
   * Get a short description of this payment method
   */
  abstract getDescription(): string;

  /**
   * Get instructions for how to use this payment method
   * This can include configuration values from the admin setup
   * @param config The configuration for this payment method
   */
  abstract getInstructions(config: TConfig): string;

  /**
   * Validate that the configuration is valid
   * @param config The configuration to validate
   */
  abstract validateConfig(config: TConfig): boolean;

  /**
   * Check if this payment method is available in the given region
   * @param region The region to check
   */
  isAvailableInRegion(region: PaymentRegion): boolean {
    return this.supportedRegions.includes(region) || this.supportedRegions.includes("global");
  }
}
